import { onScopeDispose, shallowRef, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { createClient as createFetchClient, type Config } from '@/opendoors-api/client/client'

import { useAuth } from '@/auth/useAuth'
import { client as globalClient } from '@/opendoors-api/client/client.gen'
import { useServiceStatus } from '@/composables/useServiceStatus'

export type { Client } from '@/opendoors-api/client/client/types.gen'

/**
 * URL to API by convention, using current domain.
 */
function defaultApiUrl(currentHost = location.hostname) {
  const hostParts = currentHost.split('.')
  hostParts.unshift('api')
  const domain = hostParts.join('.')
  return `https://${domain}`
}

export const DEFAULT_BACKEND_URI = import.meta.env.VITE_APP_BACKEND_URI
  ? import.meta.env.VITE_APP_BACKEND_URI
  : defaultApiUrl()

export const useClient = createGlobalState(
  (backendURI: MaybeRefOrGetter<string> = DEFAULT_BACKEND_URI) => {
    const { getAccessToken } = useAuth()
    const { addUnavailableService } = useServiceStatus()

    // https://github.com/hey-api/openapi-ts/issues/1181
    // No better way... yet.
    // But we are in a GlobalState, so run once inside our app.
    globalClient.setConfig({
      baseUrl: toValue(backendURI),
      auth: async () => {
        // null vs undefined :/
        return (await getAccessToken()) ?? undefined
      }
    })

    // Add response interceptor to detect 503 Service Unavailable errors
    const interceptorId = globalClient.interceptors.response.use(async (response) => {
      // Check for 503 Service Unavailable (but NOT 500 or other errors)
      if (response.status === 503) {
        try {
          // Clone response to read body without consuming it
          const clonedResponse = response.clone()
          const data = await clonedResponse.json()
          const serviceName = data?.service || 'External Service'
          addUnavailableService(serviceName)
        } catch {
          // If we can't parse the response, use a generic name
          addUnavailableService('External Service')
        }
      }
      return response
    })

    onScopeDispose(() => {
      // Remove the interceptor
      globalClient.interceptors.response.eject(interceptorId)
      globalClient.setConfig({})
    })

    // Just to keep it as conventional with Vue composables.
    // also so in future the global can be removed.
    const client = shallowRef(globalClient)

    return { client }
  }
)

/**
 * Create a new fetch client for OpenDoors API backend.
 *
 * @param withCredentials - include credentials, defaults to false.
 * @param backendURI - URI of the OpenDoors API, defaults to build time env variable.
 * @returns client
 */
export function createClient(
  withCredentials = false,
  backendURI = DEFAULT_BACKEND_URI,
  override: Config = {}
) {
  return createFetchClient({
    baseUrl: backendURI,
    credentials: withCredentials ? 'include' : 'omit',
    ...override
  })
}
